import {useEffect, useRef, useState} from 'react';
import {useMutation} from 'react-query';
import {paymentProAPI} from 'api';
import {submitNrbFacialVerification} from 'api/routes/nrb';
import {Button} from 'utils';
import {ProgressBar} from 'utils/ProgressBar/ProgressBar';
import {fileToBase64, getError} from 'utils/helpers';
import {
    FacialVerificationInfo,
    FacialVerificationSuccess,
    FacialVerificationRejected,
} from './FacialVerificationInfo';

export const PHASES = {
    INFO: 'info',
    CAPTURE: 'capture',
    VERIFY: 'verify',
    SUCCESS: 'success',
    REJECTED: 'rejected',
};

const RECORD_MS = 5000;

const CAMERA_CONSTRAINTS = [
    {video: {facingMode: {ideal: 'user'}}, audio: false},
    {video: {facingMode: 'user'}, audio: false},
    {video: true, audio: false},
];

const MP4_MIME_TYPES = [
    'video/mp4',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1',
    'video/mp4;codecs=h264',
];

const getUserMediaLegacy = () =>
    navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;

const requestStream = constraints => {
    if (navigator.mediaDevices?.getUserMedia)
        return navigator.mediaDevices.getUserMedia(constraints);

    const legacyGetUserMedia = getUserMediaLegacy();
    if (legacyGetUserMedia) {
        return new Promise((resolve, reject) =>
            legacyGetUserMedia.call(navigator, constraints, resolve, reject)
        );
    }
    return Promise.reject(new Error('getUserMedia is not supported on this device.'));
};

const getCameraErrorMessage = err => {
    switch (err?.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
            return 'Camera permission was blocked. Allow camera access in your browser settings and try again.';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return 'No camera was found on this device.';
        case 'NotReadableError':
        case 'TrackStartError':
            return 'Camera is in use by another app. Close other apps using the camera and try again.';
        case 'OverconstrainedError':
            return 'This device could not start the camera with the requested settings.';
        default:
            return 'Camera could not be started. Check permissions and try again.';
    }
};

const getSupportedMimeType = () => MP4_MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type));

export const FacialVerificationContent = ({token, merchant}) => {
    const [phase, setPhase] = useState(
        merchant?.facial_verification_submitted ? PHASES.SUCCESS : PHASES.INFO
    );
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState(3);
    const [recording, setRecording] = useState(false);
    const [progress, setProgress] = useState(0);
    const [cameraError, setCameraError] = useState();
    const [recordError, setRecordError] = useState();
    const [submitError, setSubmitError] = useState();

    const videoRef = useRef();
    const streamRef = useRef();
    const recorderRef = useRef();
    const stopTimeoutRef = useRef();
    const rafRef = useRef();

    const stopCamera = () => {
        streamRef.current?.getTracks()?.forEach(track => track.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
    };

    const clearTimers = () => {
        clearTimeout(stopTimeoutRef.current);
        cancelAnimationFrame(rafRef.current);
    };

    const attachStream = stream => {
        const el = videoRef.current;
        if (!el) return;
        el.srcObject = stream;
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
        el.muted = true;
        el.play();
    };

    const startCamera = async () => {
        setCameraError();

        if (!window.isSecureContext) {
            setLoading(false);
            setCameraError(
                'Camera requires a secure connection (HTTPS). Open this page using https://.'
            );
            return;
        }

        let lastError;
        for (const constraints of CAMERA_CONSTRAINTS) {
            try {
                const stream = await requestStream(constraints);
                streamRef.current = stream;
                setLoading(false);
                attachStream(stream);
                return;
            } catch (err) {
                lastError = err;
            }
        }
        setLoading(false);
        setCameraError(getCameraErrorMessage(lastError));
    };

    useEffect(() => {
        if (phase === PHASES.CAPTURE) startCamera();
        // eslint-disable-next-line
    }, [phase]);

    useEffect(() => {
        return () => {
            stopCamera();
            clearTimers();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        let countdownTimeout;
        if (countdown > 0 && !loading && !cameraError) {
            countdownTimeout = setTimeout(() => setCountdown(prev => prev - 1), 1000);
        }
        return () => clearTimeout(countdownTimeout);
    }, [countdown, loading, cameraError]);

    const animateProgress = () => {
        const start = performance.now();
        const tick = now => {
            const pct = Math.min(100, ((now - start) / RECORD_MS) * 100);
            setProgress(pct);
            if (pct < 100) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    const startRecording = () => {
        const mimeType = getSupportedMimeType();
        if (!mimeType) {
            setRecordError(
                'This browser cannot record MP4 video. Try Safari on iPhone/iPad or Safari on Mac.'
            );
            return;
        }
        setRecordError();

        const chunks = [];
        const recorder = new MediaRecorder(streamRef.current, {
            mimeType,
            videoBitsPerSecond: 2_500_000,
        });
        recorder.ondataavailable = e => {
            if (e.data?.size) chunks.push(e.data);
        };
        recorder.onstop = () => {
            const blob = new Blob(chunks, {type: 'video/mp4'});
            const file = new File([blob], 'liveness.mp4', {type: 'video/mp4'});
            stopCamera();
            setRecording(false);
            handleVerification(file);
        };

        recorderRef.current = recorder;
        recorder.start(250);
        setProgress(0);
        setRecording(true);
        animateProgress();
        stopTimeoutRef.current = setTimeout(() => recorder.stop(), RECORD_MS);
    };

    const reset = () => {
        stopCamera();
        clearTimers();
        recorderRef.current = null;
        setProgress(0);
        setCountdown(3);
        setLoading(true);
        setRecording(false);
        setCameraError();
        setRecordError();
        setPhase(PHASES.CAPTURE);
    };

    const proceedToCapture = () => setPhase(PHASES.CAPTURE);

    const {mutate: submitVerification} = useMutation(
        async file => {
            const facial_verification_video = await fileToBase64(file);
            const response = await paymentProAPI.post(submitNrbFacialVerification, {
                token,
                facial_verification_video,
            });
            return response;
        },
        {
            onSuccess: async ({data}) => {
                console.log('data', data);
                const {facial_verification_submitted} = data?.data || {};

                if (!facial_verification_submitted) {
                    setPhase(PHASES.REJECTED);
                    return;
                }
                setPhase(PHASES.SUCCESS);
            },
            onError: error => {
                const errorData = error?.response?.data?.errorData;
                errorData && setSubmitError(errorData);
                setPhase(PHASES.REJECTED);
            },
        }
    );

    const handleVerification = file => {
        setPhase(PHASES.VERIFY);
        setSubmitError();
        submitVerification(file);
    };

    return (
        <>
            {phase === PHASES.INFO && (
                <FacialVerificationInfo merchant={merchant} onProceed={proceedToCapture} />
            )}

            {(phase === PHASES.CAPTURE || phase === PHASES.VERIFY) && (
                <div className="content-header">
                    <strong>Fit your face in the oval frame to record</strong>
                </div>
            )}

            {(phase === PHASES.CAPTURE || phase === PHASES.VERIFY) && (
                <>
                    <div className="video-frame py-5 mt-4">
                        <div className="frame-edge" />
                        <div className="frame-edge right" />
                        <div className="frame-edge bottom-left" />
                        <div className="frame-edge bottom-right" />

                        <div className="flex__center">
                            {phase === PHASES.VERIFY ? (
                                <div className="verification-status">
                                    <div className="text-center">
                                        <h4 className="heading3">Verifying… Please Wait</h4>
                                    </div>
                                </div>
                            ) : loading ? (
                                <div className="video-frame-loading flex__center">
                                    <h4>Loading... Please Wait</h4>
                                </div>
                            ) : (
                                <ProgressBar progress={recording ? progress : 0}>
                                    <video ref={videoRef} muted autoPlay playsInline>
                                        Video stream not available.
                                    </video>
                                </ProgressBar>
                            )}
                        </div>
                    </div>

                    {cameraError && <p className="error-text">{cameraError}</p>}
                    {recordError && <p className="error-text">{recordError}</p>}
                </>
            )}

            {phase === PHASES.SUCCESS && <FacialVerificationSuccess />}

            {phase === PHASES.REJECTED && (
                <FacialVerificationRejected onRedo={reset} data={submitError} />
            )}

            {phase === PHASES.CAPTURE && (
                <Button
                    name={
                        recording ? 'Facial verification in progress' : 'Start Facial Verification'
                    }
                    cls="btn--sm btn--primary--bordered ms-5 px-5"
                    disabled={countdown !== 0 || progress > 1 || !!cameraError || !!recordError}
                    handleClick={startRecording}
                />
            )}
        </>
    );
};
